import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Banknote, Award } from "lucide-react";
import { pickTier } from "@/lib/loyalty";
import { useCampaign } from "@/context/CampaignContext";
import { LocationPicker } from "@/components/LocationPicker";

export default function Checkout() {
  const { t, lang } = useLanguage();
  const { items, total, clear } = useCart();
  const { user } = useAuth();
  const { campaign, inCampaign } = useCampaign();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    shipping_name: "", shipping_phone: "", shipping_email: "", shipping_address: "", shipping_city: "", notes: "",
    payment_method: "cod", coupon_code: "",
  });

  // Prefill from logged-in user profile
  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        shipping_email: f.shipping_email || user.email || "",
        shipping_name: f.shipping_name || user.name || "",
      }));
    }
  }, [user]);
  const [busy, setBusy] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null); // { code, rate }
  const [location, setLocation] = useState(null);

  const [loyaltyCfg, setLoyaltyCfg] = useState({ point_value_aed: 0.2, min_redeem: 5, tiers: [] });
  const [usePoints, setUsePoints] = useState(false);
  const [redeemPoints, setRedeemPoints] = useState(0);
  useEffect(() => {
    api.get("/loyalty-config").then(({ data }) => setLoyaltyCfg(data)).catch(() => {});
  }, []);

  const VALID_COUPONS = { WELCOME10: 0.10 };
  const discount = appliedCoupon ? total * appliedCoupon.rate : 0;
  const afterCoupon = total - discount;
  const userTier = user ? pickTier(loyaltyCfg.tiers || [], user.points_lifetime ?? user.points ?? 0) : null;
  const tierPct = userTier?.discount_percent || 0;
  const tierDiscount = +((total * tierPct) / 100).toFixed(2);
  const afterTier = Math.max(0, afterCoupon - tierDiscount);
  const pointsAvailable = user?.points || 0;
  const pointValue = loyaltyCfg.point_value_aed || 0;
  const maxRedeemable = Math.min(pointsAvailable, pointValue > 0 ? Math.floor(afterTier / pointValue) : 0);
  const effectiveRedeem = usePoints ? Math.min(redeemPoints, maxRedeemable) : 0;
  const pointsDiscount = +(effectiveRedeem * pointValue).toFixed(2);
  const finalTotal = Math.max(0, afterTier - pointsDiscount);
  const tierMult = userTier?.multiplier || 1;
  const pointsToEarn = Math.round(items.reduce((s, i) => {
    const campMult = inCampaign(i.product_id) ? (campaign.multiplier || 1) : 1;
    return s + (i.points || 0) * i.quantity * Math.max(tierMult, campMult);
  }, 0));

  const applyCoupon = () => {
    const code = couponInput.trim().toUpperCase();
    if (VALID_COUPONS[code]) {
      setAppliedCoupon({ code, rate: VALID_COUPONS[code] });
      setForm((f) => ({ ...f, coupon_code: code }));
      toast.success(t("couponApplied"));
    } else {
      toast.error(t("couponInvalid"));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error(t("emptyCart"));
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
        home_location: location,
        points_to_redeem: (user && usePoints && effectiveRedeem >= loyaltyCfg.min_redeem) ? effectiveRedeem : 0,
      };
      const { data } = await api.post("/orders/checkout", payload);
      if (data.payment_method === "card" && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        clear();
        navigate(`/order-success?order_id=${data.order_id}&cod=1`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 page-enter">
      <h1 className="font-display text-4xl mb-10">{t("checkout")}</h1>

      <div className="grid lg:grid-cols-3 gap-10">
        <form onSubmit={submit} className="lg:col-span-2 space-y-5" data-testid="checkout-form">
          <h2 className="font-display text-xl mb-4">{t("shippingDetails")}</h2>

          <div className="grid sm:grid-cols-2 gap-4">
            <input
              required value={form.shipping_name}
              onChange={(e) => setForm({ ...form, shipping_name: e.target.value })}
              placeholder={t("fullName")} className="input-luxe" data-testid="checkout-name-input"
            />
            <input
              required value={form.shipping_phone}
              onChange={(e) => setForm({ ...form, shipping_phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
              placeholder="05XXXXXXXX" dir="ltr" pattern="^05[024568][0-9]{7}$" inputMode="numeric" maxLength={10}
              title="أدخل رقم إماراتي صحيح: 05X XXX XXXX"
              className="input-luxe" data-testid="checkout-phone-input"
            />
          </div>

          <input
            required type="email" value={form.shipping_email}
            onChange={(e) => setForm({ ...form, shipping_email: e.target.value })}
            placeholder={t("email")} dir="ltr" autoCapitalize="none" spellCheck={false}
            className="input-luxe" data-testid="checkout-email-input"
          />

          <input
            required value={form.shipping_address}
            onChange={(e) => setForm({ ...form, shipping_address: e.target.value })}
            placeholder={t("address")} className="input-luxe" data-testid="checkout-address-input"
          />
          <input
            required value={form.shipping_city}
            onChange={(e) => setForm({ ...form, shipping_city: e.target.value })}
            placeholder={t("city")} className="input-luxe" data-testid="checkout-city-input"
          />
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder={t("notes")} rows={3} className="input-luxe resize-none" data-testid="checkout-notes-input"
          />

          <div className="pt-3">
            <LocationPicker value={location} onChange={setLocation} />
          </div>

          <div className="pt-4">
            <h2 className="font-display text-xl mb-4">{t("paymentMethod")}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <label
                className={`cursor-pointer p-5 border rounded-xl flex items-center gap-3 transition-all ${
                  form.payment_method === "cod" ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-[#27272A]"
                }`}
                data-testid="payment-cod-label"
              >
                <input
                  type="radio" name="pm" value="cod" checked={form.payment_method === "cod"}
                  onChange={() => setForm({ ...form, payment_method: "cod" })}
                  className="accent-[#D4AF37]" data-testid="payment-cod-radio"
                />
                <Banknote size={20} className="text-[#D4AF37]" />
                <span>{t("cod")}</span>
              </label>

              <label
                className={`cursor-pointer p-5 border rounded-xl flex items-center gap-3 transition-all ${
                  form.payment_method === "card" ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-[#27272A]"
                }`}
                data-testid="payment-card-label"
              >
                <input
                  type="radio" name="pm" value="card" checked={form.payment_method === "card"}
                  onChange={() => setForm({ ...form, payment_method: "card" })}
                  className="accent-[#D4AF37]" data-testid="payment-card-radio"
                />
                <CreditCard size={20} className="text-[#D4AF37]" />
                <div className="flex-1">
                  <div>{t("card")}</div>
                  <div className="text-[10px] text-[#9CA3AF] mt-0.5 num-ltr tracking-wider">VISA · Mastercard · AMEX</div>
                </div>
              </label>
            </div>
          </div>

          <button
            type="submit"
            disabled={busy || items.length === 0}
            data-testid="checkout-place-order-btn"
            className="btn-gold w-full py-4 rounded-full mt-6"
          >
            {busy ? "..." : t("placeOrder")}
          </button>
          {pointsToEarn > 0 && (
            <p className="text-[11px] text-[#D4AF37] text-center mt-3 num-ltr inline-flex items-center justify-center gap-1.5 w-full" data-testid="points-earn-hint">
              <Award size={12} /> {t("earnPointsHint").replace("{n}", pointsToEarn)}
            </p>
          )}
        </form>

        <aside className="bg-[#121214] border border-[#27272A] rounded-2xl p-6 h-fit sticky top-24">
          <h3 className="font-display text-lg mb-5">{t("orderSummary")}</h3>
          <div className="space-y-3 mb-5 max-h-[300px] overflow-y-auto">
            {items.map((i) => (
              <div key={i.product_id} className="flex justify-between text-sm">
                <span className="text-[#F3F4F6]/85">
                  {(lang === "ar" ? i.name_ar : i.name_en)} <span className="text-[#9CA3AF] num-ltr">× {i.quantity}</span>
                </span>
                <span className="text-[#D4AF37] num-ltr">{(i.price * i.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>

          {/* Coupon */}
          <div className="mb-5">
            {!appliedCoupon ? (
              <div className="flex gap-2">
                <input
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value)}
                  placeholder={t("couponCode")}
                  className="input-luxe flex-1 py-2 text-sm"
                  dir="ltr"
                  data-testid="coupon-input"
                />
                <button
                  type="button"
                  onClick={applyCoupon}
                  data-testid="coupon-apply-btn"
                  className="btn-outline-gold px-4 py-2 rounded-lg text-xs whitespace-nowrap"
                >
                  {t("apply")}
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 bg-[#D4AF37]/8 border border-[#D4AF37]/40 rounded-lg" data-testid="coupon-applied-banner">
                <span className="text-xs text-[#D4AF37] font-medium num-ltr">{appliedCoupon.code}</span>
                <button
                  type="button"
                  onClick={() => { setAppliedCoupon(null); setForm((f) => ({ ...f, coupon_code: "" })); setCouponInput(""); }}
                  data-testid="coupon-remove-btn"
                  className="text-xs text-[#9CA3AF] hover:text-red-400"
                >
                  ×
                </button>
              </div>
            )}
          </div>

          {/* Loyalty points redemption */}
          {user && pointsAvailable > 0 && (
            <div className="mb-5 p-3 bg-[#121214] border border-[#D4AF37]/30 rounded-lg space-y-2" data-testid="points-redeem-box">
              <div className="flex items-center justify-between text-xs">
                <span className="inline-flex items-center gap-1.5 text-[#D4AF37]"><Award size={14} /> {t("pointsBalance")}</span>
                <span className="num-ltr text-[#D4AF37] font-medium">{pointsAvailable}</span>
              </div>
              {maxRedeemable >= loyaltyCfg.min_redeem ? (
                <>
                  <label className="flex items-center gap-2 text-xs text-[#F3F4F6]/85 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={usePoints}
                      onChange={(e) => { setUsePoints(e.target.checked); setRedeemPoints(e.target.checked ? maxRedeemable : 0); }}
                      className="accent-[#D4AF37]"
                      data-testid="use-points-toggle"
                    />
                    {t("usePointsToggle")}
                  </label>
                  {usePoints && (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={loyaltyCfg.min_redeem}
                        max={maxRedeemable}
                        value={redeemPoints}
                        onChange={(e) => setRedeemPoints(Math.max(0, Math.min(maxRedeemable, parseInt(e.target.value) || 0)))}
                        className="input-luxe flex-1 py-1.5 text-sm"
                        dir="ltr"
                        data-testid="redeem-points-input"
                      />
                      <span className="text-[10px] text-[#9CA3AF] whitespace-nowrap num-ltr">/ {maxRedeemable}</span>
                    </div>
                  )}
                  <div className="text-[10px] text-[#9CA3AF] num-ltr" dir="ltr">
                    {t("redeemPointsHint").replace("{v}", pointValue).replace("{m}", loyaltyCfg.min_redeem)}
                  </div>
                </>
              ) : (
                <div className="text-[10px] text-[#9CA3AF]">{t("pointsTooFew").replace("{m}", loyaltyCfg.min_redeem)}</div>
              )}
            </div>
          )}

          <div className="divider-gold mb-4" />

          <div className="space-y-2 mb-3">
            <div className="flex justify-between text-sm">
              <span className="text-[#9CA3AF]">{t("subtotal")}</span>
              <span className="num-ltr">{total.toFixed(2)}</span>
            </div>
            {appliedCoupon && (
              <div className="flex justify-between text-sm" data-testid="discount-row">
                <span className="text-[#10B981]">{t("discount")} ({appliedCoupon.code})</span>
                <span className="text-[#10B981] num-ltr">−{discount.toFixed(2)}</span>
              </div>
            )}
            {tierDiscount > 0 && userTier && (
              <div className="flex justify-between text-sm" data-testid="tier-discount-row">
                <span className="text-[#10B981]">
                  {t("tierDiscountRow")} ({lang === "ar" ? userTier.name_ar : userTier.name_en} · {tierPct}%)
                </span>
                <span className="text-[#10B981] num-ltr">−{tierDiscount.toFixed(2)}</span>
              </div>
            )}
            {effectiveRedeem > 0 && (
              <div className="flex justify-between text-sm" data-testid="points-discount-row">
                <span className="text-[#10B981]">{t("pointsDiscount")} ({effectiveRedeem} {t("points")})</span>
                <span className="text-[#10B981] num-ltr">−{pointsDiscount.toFixed(2)}</span>
              </div>
            )}
          </div>

          <div className="flex justify-between items-baseline pt-3 border-t border-[#27272A]">
            <span className="text-[#9CA3AF] text-sm">{t("total")}</span>
            <span className="font-display text-2xl text-[#D4AF37] num-ltr" data-testid="checkout-total">
              {finalTotal.toFixed(2)} <span className="text-sm text-[#9CA3AF]">{t("currency")}</span>
            </span>
          </div>
        </aside>
      </div>
    </div>
  );
}
